import { useEffect, useState } from "react";
import axios from "axios";
import { motion } from "framer-motion";
import { toast } from "sonner";
import {
    Phone,
    Mail,
    MessageCircle,
    Clock,
    ShieldCheck,
    CheckCircle2,
    Loader2,
    Calculator as CalculatorIcon,
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const MORTGAGE_TYPES = [
    "Hipoteca 100%",
    "Hipoteca 95%",
    "Hipoteca 90%",
    "Hipoteca joven",
    "Hipoteca Premium",
    "Sin vinculaciones",
    "Segunda vivienda",
    "Capital privado",
    "Reunificación de deudas",
    "Necesito liquidez",
    "Aún no lo sé",
];

const initial = {
    name: "",
    email: "",
    phone: "",
    amount: "",
    mortgage_type: "",
    message: "",
};

const inputCls =
    "w-full rounded-xl border border-slate-200 bg-white px-4 py-3 text-[15px] text-slate-800 placeholder:text-slate-400 outline-none transition-[border-color,box-shadow] duration-200 focus:border-emerald-500 focus:ring-4 focus:ring-emerald-500/10";

export default function LeadForm() {
    const [form, setForm] = useState(initial);
    const [simulation, setSimulation] = useState("");
    const [sending, setSending] = useState(false);
    const [sent, setSent] = useState(false);

    useEffect(() => {
        const onPrefill = (e) => {
            setForm((f) => ({
                ...f,
                amount: e.detail.amount || f.amount,
                mortgage_type: MORTGAGE_TYPES.includes(e.detail.mortgage_type)
                    ? e.detail.mortgage_type
                    : f.mortgage_type,
            }));
            if (e.detail.simulation) setSimulation(e.detail.simulation);
        };
        window.addEventListener("kp:prefill", onPrefill);
        return () => window.removeEventListener("kp:prefill", onPrefill);
    }, []);

    const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

    const submit = async (e) => {
        e.preventDefault();
        setSending(true);
        try {
            await axios.post(`${API}/leads`, {
                name: form.name.trim(),
                email: form.email.trim(),
                phone: form.phone.trim(),
                amount: form.amount || null,
                mortgage_type: form.mortgage_type || null,
                message: form.message.trim() || null,
                simulation: simulation || null,
            });
            setSent(true);
            setForm(initial);
            setSimulation("");
            toast.success("¡Solicitud recibida! Te contactaremos en menos de 24h.");
        } catch (err) {
            toast.error("No se pudo enviar la solicitud. Inténtalo de nuevo.");
        } finally {
            setSending(false);
        }
    };

    return (
        <section
            id="contacto"
            data-testid="lead-form-section"
            className="bg-[#F5F7FA] py-20 lg:py-28 px-4 sm:px-6 lg:px-8"
        >
            <div className="max-w-7xl mx-auto">
                <motion.div
                    initial={{ opacity: 0, y: 40 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true, margin: "-80px" }}
                    transition={{ duration: 0.7 }}
                    className="grid lg:grid-cols-5 rounded-[2rem] overflow-hidden shadow-2xl shadow-slate-300/50 border border-slate-200"
                >
                    <div className="lg:col-span-2 relative bg-[#112338] noise-overlay p-8 sm:p-12 flex flex-col">
                        <div className="absolute inset-0 blueprint-grid opacity-50" />
                        <div className="relative flex-1">
                            <img
                                src="/assets/logo-white-trim.png"
                                alt="Keypoteca"
                                className="h-9 w-auto"
                            />
                            <h2 className="mt-8 font-display text-2xl sm:text-3xl font-bold tracking-tight text-white text-balance">
                                Consigue la mejor hipoteca hoy mismo
                            </h2>
                            <p className="mt-4 text-slate-300 leading-relaxed">
                                Déjanos tus datos y un asesor te contactará en
                                menos de 24 horas, sin compromiso.
                            </p>

                            <ul className="mt-8 space-y-4">
                                {[
                                    [ShieldCheck, "Estudio 100% gratuito"],
                                    [Clock, "Respuesta en menos de 24h"],
                                    [CheckCircle2, "Sin compromiso"],
                                ].map(([Icon, txt]) => (
                                    <li
                                        key={txt}
                                        className="flex items-center gap-3 text-slate-200 text-[15px]"
                                    >
                                        <span className="flex items-center justify-center w-9 h-9 rounded-lg bg-emerald-500/15 text-emerald-400">
                                            <Icon size={17} />
                                        </span>
                                        {txt}
                                    </li>
                                ))}
                            </ul>
                        </div>

                        <div className="relative mt-10 pt-8 border-t border-white/10 space-y-3 text-[15px]">
                            <a
                                data-testid="contact-phone"
                                href="tel:+34622344472"
                                className="flex items-center gap-3 text-slate-300 hover:text-white transition-colors"
                            >
                                <Phone size={16} className="text-emerald-400" />
                                +34 622 344 472
                            </a>
                            <a
                                data-testid="contact-email"
                                href="mailto:hola@keypoteca.es"
                                className="flex items-center gap-3 text-slate-300 hover:text-white transition-colors"
                            >
                                <Mail size={16} className="text-emerald-400" />
                                hola@keypoteca.es
                            </a>
                            <a
                                data-testid="contact-whatsapp"
                                href="https://wa.me/34622344472"
                                target="_blank"
                                rel="noopener noreferrer"
                                className="flex items-center gap-3 text-slate-300 hover:text-white transition-colors"
                            >
                                <MessageCircle
                                    size={16}
                                    className="text-emerald-400"
                                />
                                WhatsApp directo
                            </a>
                        </div>
                    </div>

                    <div id="lead-form-fields" className="lg:col-span-3 bg-white p-8 sm:p-12">
                        {sent ? (
                            <div
                                data-testid="lead-success-message"
                                className="h-full flex flex-col items-center justify-center text-center py-16"
                            >
                                <span className="flex items-center justify-center w-16 h-16 rounded-full bg-emerald-500/10 text-emerald-500">
                                    <CheckCircle2 size={34} />
                                </span>
                                <h3 className="mt-6 font-display text-2xl font-bold text-[#0F172A]">
                                    ¡Solicitud enviada!
                                </h3>
                                <p className="mt-3 text-slate-600 max-w-sm">
                                    Un asesor hipotecario revisará tu caso y te
                                    contactará en menos de 24 horas laborables.
                                </p>
                                <button
                                    data-testid="lead-send-another"
                                    onClick={() => setSent(false)}
                                    className="mt-8 rounded-full border border-slate-200 px-6 py-3 text-sm font-semibold text-[#1F3B5B] hover:border-[#1F3B5B] transition-colors"
                                >
                                    Enviar otra solicitud
                                </button>
                            </div>
                        ) : (
                            <form
                                data-testid="lead-capture-form"
                                onSubmit={submit}
                                className="grid sm:grid-cols-2 gap-5"
                            >
                                <div>
                                    <label className="block text-sm font-medium text-slate-600 mb-2">
                                        Nombre completo *
                                    </label>
                                    <input
                                        data-testid="lead-name-input"
                                        required
                                        minLength={2}
                                        value={form.name}
                                        onChange={set("name")}
                                        placeholder="María García"
                                        className={inputCls}
                                    />
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-slate-600 mb-2">
                                        Email *
                                    </label>
                                    <input
                                        data-testid="lead-email-input"
                                        required
                                        type="email"
                                        value={form.email}
                                        onChange={set("email")}
                                        placeholder="maria@email.com"
                                        className={inputCls}
                                    />
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-slate-600 mb-2">
                                        Teléfono *
                                    </label>
                                    <input
                                        data-testid="lead-phone-input"
                                        required
                                        type="tel"
                                        minLength={6}
                                        value={form.phone}
                                        onChange={set("phone")}
                                        placeholder="600 123 456"
                                        className={inputCls}
                                    />
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-slate-600 mb-2">
                                        Importe estimado (€)
                                    </label>
                                    <input
                                        data-testid="lead-amount-input"
                                        type="number"
                                        min={0}
                                        value={form.amount}
                                        onChange={set("amount")}
                                        placeholder="200.000"
                                        className={inputCls}
                                    />
                                </div>
                                <div className="sm:col-span-2">
                                    <label className="block text-sm font-medium text-slate-600 mb-2">
                                        Tipo de hipoteca
                                    </label>
                                    <select
                                        data-testid="lead-type-select"
                                        value={form.mortgage_type}
                                        onChange={set("mortgage_type")}
                                        className={inputCls}
                                    >
                                        <option value="">Selecciona una opción</option>
                                        {MORTGAGE_TYPES.map((t) => (
                                            <option key={t} value={t}>
                                                {t}
                                            </option>
                                        ))}
                                    </select>
                                </div>
                                <div className="sm:col-span-2">
                                    <label className="block text-sm font-medium text-slate-600 mb-2">
                                        Mensaje
                                    </label>
                                    <textarea
                                        data-testid="lead-message-input"
                                        rows={4}
                                        value={form.message}
                                        onChange={set("message")}
                                        placeholder="Cuéntanos tu caso: vivienda que buscas, ahorros, plazos..."
                                        className={`${inputCls} resize-none`}
                                    />
                                </div>

                                {simulation && (
                                    <div
                                        data-testid="lead-simulation-recap"
                                        className="sm:col-span-2 rounded-2xl border border-emerald-200 bg-emerald-50/60 p-5"
                                    >
                                        <p className="flex items-center gap-2 font-mono text-[11px] uppercase tracking-[0.2em] text-emerald-700">
                                            <CalculatorIcon size={14} />
                                            Tu simulación se adjuntará a la solicitud
                                        </p>
                                        <p className="mt-2 text-[13px] text-slate-600 whitespace-pre-line leading-relaxed">
                                            {simulation}
                                        </p>
                                    </div>
                                )}

                                <div className="sm:col-span-2">
                                    <button
                                        data-testid="lead-submit-btn"
                                        type="submit"
                                        disabled={sending}
                                        className="w-full inline-flex items-center justify-center gap-2 rounded-full bg-emerald-500 hover:bg-emerald-600 disabled:opacity-60 text-white font-bold px-6 py-4 text-base transition-[background-color,transform] duration-200 hover:-translate-y-0.5"
                                    >
                                        {sending ? (
                                            <>
                                                <Loader2
                                                    size={18}
                                                    className="animate-spin"
                                                />
                                                Enviando...
                                            </>
                                        ) : (
                                            "Solicitar estudio gratuito"
                                        )}
                                    </button>
                                    <p className="mt-3 text-xs text-slate-400 text-center">
                                        Al enviar aceptas nuestra política de
                                        privacidad. Nunca compartiremos tus
                                        datos.
                                    </p>
                                </div>
                            </form>
                        )}
                    </div>
                </motion.div>
            </div>
        </section>
    );
}
